import { Button } from "@/components/ui/baseUi/button"
import {
  SearchMultipleUserInputModal,
  type SearchMultipleUserInputModalRef,
} from "@/components/ui/forms/searchMultipleUserInput"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import { word } from "@modules/core/utils/i18n"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { type ProgramFormikType, type ProgramInfiniteInputKey } from "@modules/programs/types/programFormikType"
import { type ProgramOnOffFormikType, type ProgramOnOffInputKey } from "@modules/programs/types/programOnOffFormikType"
import { type SharedCoach, SharedCoachStatus } from "@modules/programs/types/programTypes"
import { SharedCoachView } from "@modules/sharedCoach/components/sharedCoachView"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { type FieldInputProps } from "formik"
import { Plus } from "lucide-react"
import { useCallback, useRef } from "react"
import { Text } from "@/components/ui/baseUi/text"

type SharedCoachPopUpProps = {
  className?: string
  setFieldValue: FieldValueChange<ProgramFormikType | ProgramOnOffFormikType>
  getFieldProps: (key: ProgramInfiniteInputKey | ProgramOnOffInputKey) => FieldInputProps<SharedCoach[] | undefined>
} & React.ComponentProps<"div">

export function ProgramSharedCoachForm({ setFieldValue, getFieldProps, className, ...props }: SharedCoachPopUpProps) {
  const confirmPopup = useConfirmPopup()
  const searchMultipleUserInputModalRef = useRef<SearchMultipleUserInputModalRef>(null)

  const removeSharedCoach = useCallback(
    async (profile: UserEntity) => {
      await setFieldValue(
        "sharedCoaches",
        [...(getFieldProps("sharedCoaches").value?.filter((coach: SharedCoach) => coach.user.id !== profile.id) ?? [])],
        false,
      )
    },
    [getFieldProps, setFieldValue],
  )

  const removeSharedCoachDialog = useCallback(
    (profile: UserEntity) => {
      confirmPopup.show({
        title: word("program.sharedCoach.form.remove"),
        message: (
          <div className="flex flex-col gap-4">
            <Text fontStyle="BM" className="m-auto text-textDefault-body"></Text>
            <SharedCoachView profile={profile} />
          </div>
        ),
        accept: () => removeSharedCoach(profile),
        footerProps: {
          confirmText: word("global.delete"),
        },
      })
    },
    [confirmPopup, removeSharedCoach],
  )

  const onConfirmAddSharedCoachs = useCallback(
    async (selectedMembers: UserEntity[]) => {
      await setFieldValue(
        "sharedCoaches",
        [
          ...(getFieldProps("sharedCoaches").value ?? []),
          ...selectedMembers.map((profile) => ({
            user: profile,
            role: "STANDARD",
            status: SharedCoachStatus.PENDING,
          })),
        ],
        false,
      )
    },
    [getFieldProps, setFieldValue],
  )

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      <Text className="mb-2 text-textDefault-caption" fontStyle="LS">
        {word("program.sharedCoach.title").toUpperCase()}
      </Text>

      <div className="flex flex-col gap-3">
        <Button
          variant="outline"
          onClick={(e) => {
            e.preventDefault()
            searchMultipleUserInputModalRef.current?.open()
          }}
          className="w-full"
        >
          {word("program.sharedCoach.form.add")}
          <Plus />
        </Button>
        {getFieldProps("sharedCoaches").value?.map((coach: SharedCoach) => (
          <SharedCoachView
            key={coach.user.id}
            profile={coach.user}
            status={coach.status}
            isAuthor
            isEditing
            onRemove={() => removeSharedCoachDialog(coach.user)}
          />
        ))}
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("program.sharedCoach.form.description")}
        </Text>
      </div>

      <SearchMultipleUserInputModal
        title={word("program.sharedCoach.form.add")}
        ref={searchMultipleUserInputModalRef}
        values={getFieldProps("sharedCoaches").value?.map((item) => item.user.id) ?? []}
        emptyDescription={word("program.sharedCoach.emptyView.description")}
        searchPlaceholder={word("program.sharedCoach.form.search")}
        onConfirm={onConfirmAddSharedCoachs}
      />
    </div>
  )
}
